import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { SEO } from '@/components/SEO';
import { SocialShare } from '@/components/SocialShare';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Calendar, Clock, User } from 'lucide-react';

const ShadowSideGoodVibes = () => {
  const article = {
    title: "The Shadow Side of \"Good Vibes Only\": Rethinking the Modern Wellness Culture",
    date: "September 27, 2025",
    readTime: "12 min read",
    author: "Piotr",
    category: "Wellness & Mental Health",
    image: "/lovable-uploads/shadow-side-good-vibes.jpg",
    imageCredit: "Samuel Austin / Unplush"
  };

  return (
    <>
      <SEO 
        title={article.title}
        description="Wellness culture loves light. But somewhere along the way, the pursuit of wellness has turned into an obligation to feel good. Exploring toxic positivity and the path to authentic emotional health."
        canonicalUrl="https://mysticforest.lovable.app/blog/shadow-side-good-vibes"
        ogType="article"
        ogImage={`https://mysticforest.lovable.app${article.image}`}
        article={{
          publishedTime: "2025-01-15T00:00:00Z",
          author: article.author,
          section: article.category,
          tags: ["toxic positivity", "emotional health", "wellness culture", "emotional agility", "mental health", "authenticity", "emotional suppression"]
        }}
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <article className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <Breadcrumbs items={[
              { name: "Articles", url: "/blog" },
              { name: article.title, url: "/blog/shadow-side-good-vibes" }
            ]} />
            
            {/* Article Header */}
            <header className="mb-12">
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <time dateTime="2025-09-27">{article.date}</time>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{article.readTime}</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>By {article.author}</span>
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl font-serif font-light mb-8 text-foreground leading-tight">
                {article.title}
              </h1>

              <div className="relative aspect-video w-full overflow-hidden rounded-lg mb-4">
                <img 
                  src={article.image}
                  alt="Person contemplating against a textured wall - exploring emotional depth"
                  className="w-full h-full object-cover grayscale"
                />
              </div>
              <p className="text-sm text-muted-foreground italic text-center">
                Photo: {article.imageCredit}
              </p>
            </header>

            {/* Article Content */}

            <div className="prose prose-lg max-w-none">
              <blockquote className="border-l-4 border-primary pl-6 italic text-xl my-8">
                "To be fully alive, fully human, and completely awake is to be continually thrown out of the nest."
                <footer className="text-sm mt-2">— Pema Chödrön, When Things Fall Apart (Boston: Shambhala Publications, 1997), p. 24</footer>
              </blockquote>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Bright Trap</h2>
              <p className="mb-6 leading-relaxed">
                Wellness culture loves light.
                It sells calm, happiness, balance, and radiant mornings in perfect harmony.
                But somewhere along the way, the pursuit of wellness has turned into an obligation to feel good.
              </p>
              <p className="mb-6 leading-relaxed">
                From yoga studios to Instagram feeds, the message echoes the same:
                <br />
                ✨ Stay positive. Think happy thoughts. Raise your vibration. ✨
              </p>
              <p className="mb-6 leading-relaxed">
                What once began as an invitation to self-care has morphed into an invisible pressure — to optimize not only our bodies, but also our emotions.
                Sadness, frustration, or anger are quietly labeled as failure.
                And so, people learn to hide them — even from themselves.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Psychology of Suppression</h2>
              <p className="mb-6 leading-relaxed">
                Research in affective science suggests that suppressing emotions doesn't make them disappear — it amplifies them.
                Dr. James Gross of Stanford University found that emotional suppression leads to increased physiological stress and weakens interpersonal connection (Journal of Personality and Social Psychology, 1998, 74(6), 1725–1738).
              </p>
              <p className="mb-6 leading-relaxed">
                By pretending we're fine, we short-circuit the natural emotional process.
                Psychologist Susan David calls this "emotional rigidity" — the inability to stay present with uncomfortable feelings.
                In her book <em>Emotional Agility</em> (New York: Avery, 2016, p. 67), she writes:
              </p>
              <blockquote className="border-l-4 border-primary pl-6 italic text-xl my-8">
                "Discomfort is the price of admission to a meaningful life."
              </blockquote>
              <p className="mb-6 leading-relaxed">
                Real resilience doesn't come from avoiding discomfort — it comes from making room for it.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Toxic Positivity: When Happiness Becomes a Standard</h2>
              <p className="mb-6 leading-relaxed">
                The term "toxic positivity" was coined by psychologists to describe the social expectation that we should maintain happiness regardless of circumstance.
                It sounds kind, but its message is cruel: if you're not happy, you're doing life wrong.
              </p>
              <p className="mb-6 leading-relaxed">
                This mindset creates shame around natural emotions — the sadness of loss, the anger of injustice, the confusion of change.
                As Brené Brown observed, "We cannot selectively numb emotions — when we numb the painful, we also numb the joyful." (<em>The Gifts of Imperfection</em>, Center City: Hazelden, 2010, p. 75).
              </p>
              <p className="mb-6 leading-relaxed">
                In other words, chasing happiness at all costs doesn't lead to joy — it leads to emotional flatness.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Beauty of Contrast</h2>
              <p className="mb-6 leading-relaxed">
                True well-being is not the absence of darkness — it's the ability to walk through it with awareness.
                Just as music needs silence and light needs shadow, our emotional lives require contrast.
              </p>
              <p className="mb-6 leading-relaxed">
                Philosopher Alain de Botton writes:
              </p>
              <blockquote className="border-l-4 border-primary pl-6 italic text-xl my-8">
                "To be mature is to accept that we will not always be happy, that our emotions will be turbulent, and that pain is not a sign of failure."
                <footer className="text-sm mt-2">— The School of Life: An Emotional Education, London: Penguin, 2019, p. 93</footer>
              </blockquote>
              <p className="mb-6 leading-relaxed">
                Emotional literacy — the ability to name and navigate our emotions — is what allows that maturity to form.
                It's not about fixing feelings, but about understanding them.
                Sadness invites reflection.
                Anger points to boundaries.
                Fear reveals care.
              </p>
              <p className="mb-6 leading-relaxed">
                When we honor these signals instead of erasing them, we live more deeply — and paradoxically, more peacefully.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">From Optimization to Acceptance</h2>
              <p className="mb-6 leading-relaxed">
                Wellness today often mimics capitalism: measurable, goal-oriented, and performance-based.
                We track our steps, sleep, heart rate, even mindfulness minutes — as if peace could be quantified.
              </p>
              <p className="mb-6 leading-relaxed">
                But emotional health isn't optimization; it's relationship.
                It's learning to sit with what is — to meet every mood without labeling it good or bad.
              </p>
              <p className="mb-6 leading-relaxed">
                Zen teacher Shunryu Suzuki once said:
              </p>
              <blockquote className="border-l-4 border-primary pl-6 italic text-xl my-8">
                "When you do something, you should burn yourself completely, like a good bonfire, leaving no trace of yourself."
                <footer className="text-sm mt-2">— Zen Mind, Beginner's Mind, New York: Weatherhill, 1970, p. 45</footer>
              </blockquote>
              <p className="mb-6 leading-relaxed">
                To live fully means to allow life to move through us completely — both its warmth and its ache.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Mystic Forest Way</h2>
              <p className="mb-6 leading-relaxed">
                At Mystic Forest, we believe emotional depth is a form of wisdom.
                Our retreats and workshops are not escapes from life — they're invitations to inhabit it more honestly.
              </p>
              <p className="mb-6 leading-relaxed">
                We create spaces where joy and sorrow can coexist, where stillness meets laughter, and where no one has to hide behind "I'm fine."
                Silence, movement, and nature help us reconnect with what's real — not just what's pleasant.
              </p>
              <p className="mb-6 leading-relaxed">
                We don't chase positivity.
                We practice presence.
                Because the goal is not to feel good all the time — it's to feel alive.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">In the End</h2>
              <p className="mb-6 leading-relaxed">
                To be whole is not to be happy — it's to be honest.
                Let's stop worshiping good vibes and start honoring the full spectrum of being human.
              </p>
              <p className="mb-6 leading-relaxed">
                Because peace is not a constant state of calm —
                <br />
                it's the courage to welcome whatever arises,
                <br />
                and to call it life.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h3 className="text-2xl font-serif font-light mt-12 mb-6">References</h3>

              <ul className="space-y-2 mb-6 text-sm">
                <li>Brown, B. (2010). <em>The Gifts of Imperfection</em>. Center City: Hazelden.</li>
                <li>Chödrön, P. (1997). <em>When Things Fall Apart</em>. Boston: Shambhala Publications.</li>
                <li>David, S. (2016). <em>Emotional Agility</em>. New York: Avery.</li>
                <li>de Botton, A. (2019). <em>The School of Life: An Emotional Education</em>. London: Penguin.</li>
                <li>Gross, J. J. (1998). Antecedent- and Response-Focused Emotion Regulation: Divergent Consequences for Experience, Expression, and Physiology. <em>Journal of Personality and Social Psychology</em>, 74(6), 1725–1738.</li>
                <li>Suzuki, S. (1970). <em>Zen Mind, Beginner's Mind</em>. New York: Weatherhill.</li>
              </ul>

              <p className="text-center text-lg italic mt-12 text-muted-foreground">
                To be whole is not to be happy — it's to be honest. Let's honor the full spectrum of being human.
              </p>
            </div>

            {/* Social Share */}
            <div className="mt-12 pt-8 border-t border-border">
              <SocialShare 
                url="https://mysticforest.lovable.app/blog/shadow-side-good-vibes"
                title={article.title}
                description="Wellness culture loves light. But somewhere along the way, the pursuit of wellness has turned into an obligation to feel good. Exploring toxic positivity and the path to authentic emotional health."
              />
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
};

export default ShadowSideGoodVibes;
